import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  OutreachCategory,
  SocialLink,
  genreOptions,
} from "../../types/outreach";
import type {
  YouTubeChannelSearchDetails,
  SpotifyPlaylistDetails,
} from "../../types/outreach";
import {
  formatLegacySocialHandles,
  resolveSocialLinks,
} from "../../utils/socialLinks";
import { SocialLinksInput } from "./SocialLinksInput";

export interface ChannelFormValues {
  channelName: string;
  channelUrl: string;
  subscriberCount: string;
  email: string;
  socialHandles: string;
  socialLinks: SocialLink[];
  viewCount: string;
  videoCount: string;
  likes: string;
  genre: string;
  lastUpdated: string;
}

export interface SpotifyPlaylistFormValues {
  playlistName: string;
  playlistUrl: string;
  curatorSocialHandle: string;
  curatorEmail: string;
  playlistFollowers: string;
  trackCount: string;
  genre: string;
  source: string;
  lastUpdated: string;
}

interface YouTubeConfirmationProps {
  open: boolean;
  category: OutreachCategory | null;
  loading: boolean;
  onClose: () => void;
  mode?: "youtube";
  channelDetails: YouTubeChannelSearchDetails | null;
  playlistDetails?: never;
  initialValues: ChannelFormValues | null;
  onConfirm: (values: ChannelFormValues) => Promise<void>;
}

interface SpotifyConfirmationProps {
  open: boolean;
  category: OutreachCategory | null;
  loading: boolean;
  onClose: () => void;
  mode: "spotify";
  channelDetails?: never;
  playlistDetails: SpotifyPlaylistDetails | null;
  initialValues: SpotifyPlaylistFormValues | null;
  onConfirm: (values: SpotifyPlaylistFormValues) => Promise<void>;
}

type ChannelConfirmationModalProps =
  | YouTubeConfirmationProps
  | SpotifyConfirmationProps;

const categoryLabels: Record<OutreachCategory, string> = {
  [OutreachCategory.REACTION_CHANNELS]: "Reaction Channels",
  [OutreachCategory.PLAYLISTING_CHANNELS]: "Playlisting Channels",
  [OutreachCategory.SPOTIFY_PLAYLISTS]: "Spotify Playlists",
  [OutreachCategory.MUSIC_BLOGS]: "Music Blogs",
  [OutreachCategory.MUSIC_INDUSTRY]: "Music Industry",
  [OutreachCategory.CAMPAIGNS]: "Campaigns",
  [OutreachCategory.MESSAGE_CORRELATIONS]: "Message Correlations",
};

const formatDateTimeLocal = (isoDate: string): string => {
  if (!isoDate) {
    return "";
  }
  const date = new Date(isoDate);
  const pad = (value: number) => value.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseDateTimeLocal = (value: string): string => {
  if (!value) {
    return new Date().toISOString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }
  return date.toISOString();
};

const YouTubeConfirmationModal: React.FC<YouTubeConfirmationProps> = ({
  open,
  category,
  channelDetails,
  initialValues,
  loading,
  onClose,
  onConfirm,
}) => {
  const [formValues, setFormValues] = useState<ChannelFormValues | null>(
    initialValues
  );

  useEffect(() => {
    if (!initialValues) {
      setFormValues(null);
      return;
    }

    const normalizedLinks = resolveSocialLinks(
      initialValues.socialLinks,
      initialValues.socialHandles
    );

    setFormValues({
      ...initialValues,
      socialLinks: normalizedLinks,
      socialHandles: formatLegacySocialHandles(normalizedLinks),
    });
  }, [initialValues]);

  const handleChange = (
    field: keyof ChannelFormValues
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormValues((prev) =>
      prev
        ? {
            ...prev,
            [field]: field === "lastUpdated" ? parseDateTimeLocal(value) : value,
          }
        : prev
    );
  };

  const formattedLastUpdated = useMemo(() => {
    if (!formValues) {
      return "";
    }
    return formatDateTimeLocal(formValues.lastUpdated);
  }, [formValues]);

  const handleConfirm = async () => {
    if (!formValues) {
      return;
    }
    const normalizedLinks = resolveSocialLinks(
      formValues.socialLinks,
      formValues.socialHandles
    );
    await onConfirm({
      ...formValues,
      socialLinks: normalizedLinks,
      socialHandles: formatLegacySocialHandles(normalizedLinks),
      lastUpdated: parseDateTimeLocal(formattedLastUpdated),
    });
  };

  const categoryLabel = category ? categoryLabels[category] : "";
  const selectedGenres = formValues?.genre
    ? formValues.genre.split("/").filter(Boolean)
    : [];

  const handleGenreChange = (
    _: React.SyntheticEvent<Element, Event>,
    newValue: string[]
  ) => {
    setFormValues((prev) =>
      prev
        ? {
            ...prev,
            genre: newValue.join("/"),
          }
        : prev
    );
  };

  const resolvedSocialLinks = useMemo(
    () => resolveSocialLinks(formValues?.socialLinks, formValues?.socialHandles),
    [formValues]
  );

  const handleSocialLinksChange = (links: SocialLink[]) => {
    setFormValues((prev) =>
      prev
        ? {
            ...prev,
            socialLinks: links,
            socialHandles: formatLegacySocialHandles(links),
          }
        : prev
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Review channel before adding</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {category && (
            <Typography variant="subtitle1" color="text.secondary">
              Adding to {categoryLabel}
            </Typography>
          )}
          {channelDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {channelDetails.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {channelDetails.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Subscribers:</strong> {channelDetails.subscriberCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Total Views:</strong>{" "}
                {channelDetails.viewCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Videos:</strong> {channelDetails.videoCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Channel ID:</strong> {channelDetails.channelId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Published:</strong>{" "}
                {new Date(channelDetails.publishedAt).toLocaleDateString()}
              </Typography>
              {channelDetails.country && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Country:</strong> {channelDetails.country}
                </Typography>
              )}
            </Box>
          )}

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Channel Name"
                fullWidth
                value={formValues?.channelName ?? ""}
                onChange={handleChange("channelName")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Channel URL"
                fullWidth
                value={formValues?.channelUrl ?? ""}
                onChange={handleChange("channelUrl")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subscriber Count"
                fullWidth
                value={formValues?.subscriberCount ?? ""}
                onChange={handleChange("subscriberCount")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={formValues?.email ?? ""}
                onChange={handleChange("email")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SocialLinksInput
                value={resolvedSocialLinks}
                legacyValue={formValues?.socialHandles ?? ""}
                onChange={handleSocialLinksChange}
                helperText="Paste @handles or URLs"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={genreOptions}
                value={selectedGenres}
                onChange={handleGenreChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Genre"
                    placeholder="Select genres"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="View Count"
                fullWidth
                value={formValues?.viewCount ?? ""}
                onChange={handleChange("viewCount")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Video Count"
                fullWidth
                value={formValues?.videoCount ?? ""}
                onChange={handleChange("videoCount")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Likes"
                fullWidth
                value={formValues?.likes ?? ""}
                onChange={handleChange("likes")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Updated"
                type="datetime-local"
                fullWidth
                value={formattedLastUpdated}
                onChange={handleChange("lastUpdated")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading || !formValues}
        >
          {loading ? "Saving..." : "Add Channel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SpotifyConfirmationModal: React.FC<SpotifyConfirmationProps> = ({
  open,
  category,
  playlistDetails,
  initialValues,
  loading,
  onClose,
  onConfirm,
}) => {
  const [formValues, setFormValues] = useState<SpotifyPlaylistFormValues | null>(
    initialValues
  );

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const handleChange = (
    field: keyof SpotifyPlaylistFormValues
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormValues((prev) =>
      prev
        ? {
            ...prev,
            [field]: field === "lastUpdated" ? parseDateTimeLocal(value) : value,
          }
        : prev
    );
  };

  const formattedLastUpdated = useMemo(() => {
    if (!formValues) {
      return "";
    }
    return formatDateTimeLocal(formValues.lastUpdated);
  }, [formValues]);

  const categoryLabel = category ? categoryLabels[category] : "";
  const selectedGenres = formValues?.genre
    ? formValues.genre.split("/").filter(Boolean)
    : [];

  const handleGenreChange = (
    _: React.SyntheticEvent<Element, Event>,
    newValue: string[]
  ) => {
    setFormValues((prev) =>
      prev
        ? {
            ...prev,
            genre: newValue.join("/"),
          }
        : prev
    );
  };

  const handleConfirm = async () => {
    if (!formValues) {
      return;
    }

    await onConfirm({
      ...formValues,
      lastUpdated: parseDateTimeLocal(formattedLastUpdated),
    });
  };

  const followers = playlistDetails?.followers?.total;
  const trackCount = playlistDetails?.tracks?.total;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Review playlist before adding</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {category && (
            <Typography variant="subtitle1" color="text.secondary">
              Adding to {categoryLabel}
            </Typography>
          )}
          {playlistDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {playlistDetails.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Curated by {playlistDetails.owner?.display_name ?? "Unknown"}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  <strong>Followers:</strong>{" "}
                  {typeof followers === "number"
                    ? followers.toLocaleString()
                    : "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tracks:</strong>{" "}
                  {typeof trackCount === "number"
                    ? trackCount.toLocaleString()
                    : "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong>{" "}
                  {playlistDetails.public ? "Public" : "Private"}
                </Typography>
                {playlistDetails.collaborative && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Collaborative</strong>
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Playlist Name"
                fullWidth
                value={formValues?.playlistName ?? ""}
                onChange={handleChange("playlistName")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Playlist URL"
                fullWidth
                value={formValues?.playlistUrl ?? ""}
                onChange={handleChange("playlistUrl")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Curator Handle"
                fullWidth
                value={formValues?.curatorSocialHandle ?? ""}
                onChange={handleChange("curatorSocialHandle")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Curator Email"
                fullWidth
                value={formValues?.curatorEmail ?? ""}
                onChange={handleChange("curatorEmail")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Followers"
                fullWidth
                value={formValues?.playlistFollowers ?? ""}
                onChange={handleChange("playlistFollowers")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Track Count"
                fullWidth
                value={formValues?.trackCount ?? ""}
                onChange={handleChange("trackCount")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={genreOptions}
                value={selectedGenres}
                onChange={handleGenreChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Genre"
                    placeholder="Select genres"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Source"
                fullWidth
                value={formValues?.source ?? ""}
                onChange={handleChange("source")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Updated"
                type="datetime-local"
                fullWidth
                value={formattedLastUpdated}
                onChange={handleChange("lastUpdated")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading || !formValues}
        >
          {loading ? "Saving..." : "Add Playlist"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ChannelConfirmationModal: React.FC<ChannelConfirmationModalProps> = (
  props
) => {
  if (props.mode === "spotify") {
    return <SpotifyConfirmationModal {...props} />;
  }

  return <YouTubeConfirmationModal {...props} />;
};

export default ChannelConfirmationModal;
